import React, { useState } from "react"
import { Col, Row, Label, Input, FormFeedback } from "reactstrap"
import Select from "react-select"
import * as yup from "yup"
import CopyToClipboard from "./CopyToClipboard"
import "./CustomYupInputForm.css"

const CustomYupInputForm = ({ label, fieldName, inputType, options = [], selectedOption = null, isMulti = false, isClearable = false, isEditing, isLoading, defaultValue, inputValue, onEdit, onSave, onChange, onCancel, yupSchema, validationValues, showCopy, showEdit, readOnlyTitle = "Read Only", renderCustomInput, renderAdditionalActions = null, inputProps = {} }) => {
    const [error, setError] = useState("")
    const rawValue = inputValue !== undefined && inputValue !== null ? inputValue : defaultValue !== undefined && defaultValue !== null ? defaultValue : isMulti ? [] : ""


    const defaultSchema = () => {
        if (inputType === "select") {
            if (isMulti) {
                let s = yup
                    .array()
                    .nullable()
                return s
            } else {
                let s = yup
                    .string()
                    .transform((v, o) => (o === "" || o == null ? null : v))
                    .nullable()
                return s
            }
        }

        if (inputType === "number") {
            let s = yup
                .number()
                .transform((v, o) => (o === "" || o == null ? null : v))
                .typeError(`${label} must be a number`)
                .nullable()
            return s
        }

        if (inputType === "date") {
            let s = yup
                .date()
                .transform((v, o) => (o === "" || o == null ? null : v))
                .typeError(`Invalid ${label.toLowerCase()}`)
                .nullable()
            return s
        }

        let s = yup
            .string()
            .transform((v, o) => (typeof o === "string" && o.trim() === "" ? null : v))
            .nullable()
        return s
    }

    const getFieldSchema = () => {
        if (!yupSchema) return defaultSchema()
        if (yupSchema?.type === "object") {
            try {
                return yup.reach(yupSchema, fieldName)
            } catch {
                return defaultSchema()
            }
        }
        return yupSchema
    }

    const handleSelectValue = () => {
        if (inputType !== "select") return null
        if (isMulti) {
            const arr = Array.isArray(rawValue) ? rawValue : []
            return options.filter((o) => arr.includes(o.value))
        }
        return options.find((o) => o.value === rawValue) || (selectedOption?.value === rawValue ? selectedOption : null)
    }


    const validateValue = async (val) => {
        let errorMessage = ""
        try {
            if (yupSchema?.type === "object" && validationValues) {
                await yupSchema.validateAt(fieldName, { ...validationValues, [fieldName]: val })
            } else {
                const fieldSchema = getFieldSchema()
                await fieldSchema.validate(val)
            }
        } catch (e) {
            errorMessage = e?.message || "Invalid value"
        }
        setError(errorMessage)
        return errorMessage
    }

    const handleTextChange = async (e) => {
        onChange(e)
        const v = e?.target?.value ?? e
        await validateValue(v)
    }

    const handleSelectChange = async (newVal) => {
        const extracted = isMulti ? Array.isArray(newVal) ? newVal.map((o) => o.value) : [] : newVal?.value ?? ""
        onChange({ target: { name: fieldName, value: extracted } })
        await validateValue(extracted)
    }

    const handleSave = async () => {
        const inputValueForValidation = inputType === "select" ? isMulti ? Array.isArray(rawValue) ? rawValue : [] : rawValue ?? "" : rawValue
        const msg = await validateValue(inputValueForValidation)
        if (msg) return
        onSave(fieldName, inputValueForValidation)
        setError("")
    }

    const handleCancel = () => {
        setError("")
        if (onCancel) onCancel()
    }

    const handleEdit = () => {
        if (!onEdit) return
        const editResult = onEdit(fieldName, defaultValue)
        if (typeof editResult === "string" && editResult) {
            setError(editResult)
            return
        }
        setError("")
    }

    return (
        <span className="custom-yup-input">
            <Row>
                <Col xs="9">
                    <Label htmlFor={`${fieldName}-input`} >{label}</Label>
                </Col>
                <Col xs="3" className="text-right custom-yup-input__actions">
                    {isLoading ? (
                        <i className="fa fa-lg fa-spinner fa-spin text-muted" title="Saving..." />
                    ) : isEditing ? (
                        <>
                            <i className="fa fa-lg fa-times text-danger cursor-pointer" onClick={handleCancel} title="Cancel" />
                            &nbsp;&nbsp;
                            <i
                                style={{ pointerEvents: error ? "none" : "auto" }}
                                className={`fa fa-lg fa-check ${error ? "text-muted" : "text-success"} cursor-pointer`}
                                onClick={!error ? handleSave : undefined} title={!error ? "Save" : error}
                            />
                        </>
                    ) : (
                        <>
                            {showEdit && <i className="fa fa-lg fa-pencil-square custom-yup-input__edit" onClick={handleEdit} title="Edit" />}
                            {showCopy && <>
                                &nbsp;&nbsp;<CopyToClipboard textToCopy={String(defaultValue || "")} displayText={label} />
                            </>}
                            {!showEdit && <>
                                &nbsp;&nbsp;<i className="fa fa-lg fa-lock" title={readOnlyTitle} />
                            </>}
                            {renderAdditionalActions && renderAdditionalActions()}
                        </>
                    )}
                </Col>
            </Row>
            <Row>
                <Col>
                    {renderCustomInput && isEditing ?
                        renderCustomInput()
                        : <>
                            {inputType === "select" ? (
                                <>
                                    <Select
                                        className="bos-custom-select"
                                        classNamePrefix="bos-select"
                                        inputId={`${fieldName}-input`}
                                        isMulti={isMulti}
                                        isClearable={isClearable}
                                        options={options}
                                        value={handleSelectValue()}
                                        onChange={handleSelectChange}
                                        isDisabled={!isEditing || isLoading}
                                        placeholder={isEditing ? `Select ${label}` : ""}
                                    />
                                    {error && <FormFeedback className="d-block">{error}</FormFeedback>}
                                </>
                            ) : (
                                <>
                                    <Input
                                        id={`${fieldName}-input`}
                                        type={inputType}
                                        name={fieldName}
                                        disabled={!isEditing || isLoading}
                                        value={rawValue}
                                        onChange={handleTextChange}
                                        onBlur={handleTextChange}
                                        invalid={isEditing && !!error}
                                        {...inputProps}
                                    />
                                    {error && (
                                        <FormFeedback className="d-block">{error}</FormFeedback>
                                    )}
                                </>
                            )}
                        </>
                    }
                </Col>
            </Row>
        </span>
    )
}

export default CustomYupInputForm
